import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, Save, Search, Trash2, UploadCloud } from 'lucide-react';
import { apiService, PackBlockDTO, PackDropEntry } from '@/services/api.service';
import { useUnsavedDraft } from '@/stores/unsavedDrafts.store';
import { PackThumbnail } from './PackThumbnail';
import { loadPackPalette, PalettePickEntry } from './packPalette';
import styles from './PackForm.module.scss';
import recipe from './RecipeEditor.module.scss';
import drop from './DropEditor.module.scss';

type Filter = 'all' | 'block' | 'item';
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'Tous' },
  { id: 'block', label: 'Blocks' },
  { id: 'item', label: 'Items' },
];

export function DropEditor({ block, groupId, onSaved }: { block: PackBlockDTO; groupId: string; onSaved?: () => void }) {
  const [entries, setEntries] = useState<PackDropEntry[]>([]);
  const [dirty, setDirty] = useState(false);
  const [requireTool, setRequireTool] = useState(false);
  const [fortune, setFortune] = useState(false);
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    let active = true;
    loadPackPalette().then((p) => active && setPalette(p));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiService.getPackDrop(groupId, block.identifier).then((r) => {
      if (!active) return;
      setEntries(r.data?.entries ?? []);
      setRequireTool(!!r.data?.requireTool);
      setFortune(!!r.data?.fortune);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [groupId, block.identifier]);

  const infoOf = useMemo(() => {
    const m = new Map<string, PalettePickEntry>();
    palette.forEach((p) => m.set(p.id, p));
    return m;
  }, [palette]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let l = filter === 'all' ? palette : palette.filter((p) => p.kind === filter);
    if (q) l = l.filter((p) => p.id.toLowerCase().includes(q));
    return l.slice(0, 160);
  }, [palette, query, filter]);

  const addEntry = (id: string) => { setEntries((e) => [...e, { item: id, count: 1, min: 1, max: 1, random: false }]); setDirty(true); };
  const updateEntry = (i: number, patch: Partial<PackDropEntry>) => { setEntries((e) => e.map((x, idx) => (idx === i ? { ...x, ...patch } : x))); setDirty(true); };
  const removeEntry = (i: number) => { setEntries((e) => e.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setFeedback(null);
    const res = await apiService.savePackDrop(groupId, {
      identifier: block.identifier,
      behaviorFile: block.sourceType === 'block' ? block.source : undefined,
      entries,
      requireTool,
      fortune,
    });
    setSaving(false);
    if (res.data?.success) {
      setDirty(false);
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
      return true;
    }
    setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec' });
    return false;
  };

  // Suivi des modifications non enregistrées → barre « Enregistrer tout » + marqueur liste.
  useUnsavedDraft({ kind: 'block', scope: 'drop', groupId, identifier: block.identifier, label: `${block.identifier} (drop)`, dirty, save });

  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    setFeedback(res.data ? { ok: res.data.success, msg: res.data.messages.join(' · ') } : { ok: false, msg: res.error || 'Échec publication' });
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader2 size={18} className={styles.spin} /> Chargement des drops…
      </div>
    );
  }

  return (
    <div className={styles.form}>
      <p className={recipe.hint}>Ce que lâche le block quand il est cassé. Vide = il se drop lui-même.</p>

      <div className={recipe.paletteBox}>
        <div className={recipe.paletteHead}>
          <div className={styles.search}>
            <Search size={14} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un block / item…" />
          </div>
          <div className={recipe.filterTabs}>
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className={`${recipe.filterTab} ${filter === f.id ? recipe.filterTabActive : ''}`} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className={recipe.palette}>
          {filtered.map((p) => (
            <button key={p.id} type="button" title={p.id} className={recipe.paletteItem} onClick={() => addEntry(p.id)}>
              {p.path ? <PackThumbnail groupId={p.groupId} path={p.path} size={34} /> : <span className={recipe.cellText}>{p.id.split(':').pop()}</span>}
            </button>
          ))}
        </div>
        <div className={recipe.brushBar}>
          <span>Clique un item / block pour l'ajouter aux drops.</span>
        </div>
      </div>

      <div className={drop.list}>
        {entries.length === 0 && <div className={drop.empty}>Aucun drop — le block se drop lui-même.</div>}
        {entries.map((e, i) => {
          const info = infoOf.get(e.item);
          return (
            <div key={i} className={drop.row}>
              {info?.path ? <PackThumbnail groupId={info.groupId} path={info.path} size={36} /> : <span className={drop.noimg}>{e.item.split(':').pop()}</span>}
              <span className={drop.itemId}>{e.item}</span>
              <label className={drop.randToggle}>
                <input type="checkbox" checked={e.random} onChange={(ev) => updateEntry(i, { random: ev.target.checked })} /> aléatoire
              </label>
              {e.random ? (
                <span className={drop.qty}>
                  <input type="number" min={1} value={e.min ?? 1} onChange={(ev) => updateEntry(i, { min: Number(ev.target.value) || 1 })} />
                  <span className={drop.dash}>–</span>
                  <input type="number" min={1} value={e.max ?? 1} onChange={(ev) => updateEntry(i, { max: Number(ev.target.value) || 1 })} />
                </span>
              ) : (
                <input className={drop.qtyOne} type="number" min={1} value={e.count ?? 1} onChange={(ev) => updateEntry(i, { count: Number(ev.target.value) || 1 })} />
              )}
              <button type="button" className={styles.stagedDel} onClick={() => removeEntry(i)} title="Retirer">
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <label className={styles.checkRow}>
        <input type="checkbox" checked={requireTool} onChange={(e) => { setRequireTool(e.target.checked); setDirty(true); }} />
        <span>Nécessite le bon outil (sinon rien ne drop) — l'outil/palier se règle dans l'onglet Propriété</span>
      </label>
      <label className={styles.checkRow}>
        <input type="checkbox" checked={fortune} onChange={(e) => { setFortune(e.target.checked); setDirty(true); }} />
        <span>Bonus Fortune (quantité augmentée selon l'enchantement)</span>
      </label>

      {feedback && (
        <div className={feedback.ok ? styles.ok : styles.error}>
          {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
        </div>
      )}

      <div className={styles.actions}>
        <button className={styles.saveBtn} onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
          {saving ? 'Enregistrement…' : 'Enregistrer les drops'}
        </button>
        <button className={styles.publishBtn} onClick={publish} disabled={publishing}>
          {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />}
          {publishing ? 'Publication…' : 'Publier sur git'}
        </button>
      </div>
    </div>
  );
}
